import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { injectStructuredData } from '@/components/feature/StructuredData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);
  const cat = categoryMap[activeSlug] || categories[0];
  const articles = getArticlesByCategory(activeSlug);

  useEffect(() => {
    if (slug && categoryMap[slug]) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    injectStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://peachhipguide.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: cat.name,
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${cat.name} - 蜜桃臀塑形指南`,
        description: cat.description,
        url: `https://peachhipguide.com/category/${activeSlug}`,
        inLanguage: 'zh-CN',
      },
    ]);
  }, [activeSlug, cat]);

  return (
    <div className="min-h-screen pt-16">
      <div className="bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: cat.name },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-5 md:gap-6 mt-4">
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-background-50 rounded-lg border border-background-200/60 p-4 lg:sticky lg:top-20">
                <h3 className="text-foreground-900 font-semibold text-sm mb-3">训练分类</h3>
                <nav className="flex flex-col gap-1">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      onClick={() => setActiveSlug(c.slug)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                        activeSlug === c.slug
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${c.icon} text-xs`}></i>
                      </span>
                      {c.name}
                    </Link>
                  ))}
                </nav>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="bg-background-50 rounded-lg border border-background-200/60 p-5 md:p-6 mb-5">
                <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2">
                  {cat.name}
                </h1>
                <p className="text-foreground-600 text-sm">{cat.description}</p>
              </div>

              {articles.length === 0 ? (
                <div className="bg-background-50 rounded-lg border border-background-200/60 p-10 text-center">
                  <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group flex flex-col sm:flex-row gap-4 bg-background-50 rounded-lg border border-background-200/60 p-4 hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="w-full sm:w-44 h-32 flex-shrink-0 rounded-md overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                            <span>{article.date}</span>
                            <span>{article.readTime}</span>
                            <span>{article.author}</span>
                          </div>
                          <h3 className="text-foreground-900 font-semibold text-sm md:text-base leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2">
                            {article.excerpt}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}